import { useEffect, useState } from "react";
import { PlusSquareOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  UploadFile,
  message,
} from "antd";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import DeleteSVG from "../../../assets/common/DeleteSVG";
import { queryKeys } from "../../../services/react-query/queryKeys";
import queryClient from "../../../services/react-query/queryClient";
import SalonSVG from "../../../assets/teamMember/SalonSVG";
import { getSuppliers } from "../../../network/supplier";
import { getAllLocations } from "../../../network/location";
import { Location } from "../../../types/location";
import { addProduct, editProduct } from "../../../network/products";
import { PlusOutlined } from "@ant-design/icons";
import ModalForm from "../../common/ModalForm";
import { productType } from "../../../types/product";
import { useIntl } from "react-intl";

interface PriceOptionProps {
  onClose: () => void;
  product: productType;
  setProduct: React.Dispatch<React.SetStateAction<productType>>;
  update?: boolean;
  productImg: UploadFile<any>;
}

function PriceOption({
  onClose,
  product,
  setProduct,
  update,
  productImg,
}: PriceOptionProps) {
  const [selectedSku, setselectedSku] = useState<any[]>(
    product?.sku_code?.map((sku: any) => {
      if (typeof sku === "string") {
        return { sku_code: sku };
      } else {
        return sku;
      }
    }) || []
  );
  // const [generateSku, setGenerateSku] = useState<any>();

  const { locationId } = useSelector(
    (state: {
      calendar: {
        locationId: any;
      };
    }) => {
      return {
        locationId: state.calendar?.locationId,
      };
    }
  );

  const [form] = Form.useForm();

  const [supplierVisible, setSupplierVisible] = useState(false);

  const { data: listSuppliers, isLoading: isLoadingSuppliers } = useQuery(
    [queryKeys.listSuppliers],
    () => getSuppliers()
  );

  const { mutate: onAddProduct, isLoading } = useMutation(
    (formData: FormData) => addProduct(product.category_id, formData),
    {
      onSuccess: (res: any) => {
        if (
          res?.response?.status === 422 ||
          res?.response?.status === 500 ||
          res?.response?.status >= 400
        ) {
          message.error(res?.response?.data?.message);
        } else {
          message.success(intl.formatMessage({ id: "productAddedSuccess" }));
          queryClient.invalidateQueries([queryKeys.listProduct]);
          onClose();
        }
      },
      onError: (error) => {
        console.log("Add Package error:", error);
        message.error("حاول مرة اخرى");
      },
    }
  );
  const { mutate: onEditProduct } = useMutation(
    (formData: FormData) =>
      editProduct(product.category_id, product.id, formData),
    {
      onSuccess: (res) => {
        console.log("Packages response:", res);
        onClose();
        message.success(intl.formatMessage({ id: "productUpdatedSuccess" }));
        queryClient.invalidateQueries([queryKeys.listProduct]);
      },
      onError: (error) => {
        console.log("Add Package error:", error);
        message.error("حاول مرة اخرى");
      },
    }
  );
  const handleRemoveSku = (indexToRemove: number) => {
    const newSkuCodes = selectedSku.filter(
      (_: any, index: number) => index !== indexToRemove
    );
    setselectedSku(newSkuCodes);
    setProduct({
      ...product,
      sku_code: newSkuCodes,
    });
  };
  useEffect(() => {
    setProduct({
      ...product,
      sku_code: selectedSku,
    });
  }, [selectedSku]);
  const onFinish = async (values: any) => {
    const selectedSkuCodes = selectedSku?.map((item: any) => item?.sku_code);

    if (selectedSkuCodes.length < 1) {
      message.error(intl.formatMessage({ id: "enterSkuCode" }));
      return;
    }
    let form_data = new FormData();

    // productInfo
    form_data.append("name[ar]", product?.name?.ar);
    form_data.append("name[en]", product?.name?.en);
    if (product?.description?.ar) {
      form_data.append("description[ar]", product?.description?.ar);
    }
    if (product?.description?.en) {
      form_data.append("description[en]", product?.description?.en);
    }
    if (product?.brand_id) {
      form_data.append("brand_id", String(product?.brand_id));
    }

    form_data.append("measure", product?.measure);
    form_data.append("amount", product?.amount);
    form_data.append("barcode", product?.barcode);
    if (productImg?.originFileObj) {
      form_data.append("image", productImg.originFileObj);
    }

    // productPriceOptions
    form_data.append("supply_price", product?.supply_price);
    form_data.append("markup", product?.markup);
    form_data.append("retail_price", product?.retail_price);
    form_data.append(
      "enable_team_member_commission",
      product?.enable_team_member_commission ? "1" : "0"
    );
    form_data.append(
      "enable_retail_sales",
      product?.enable_retail_sales ? "1" : "0"
    );
    form_data.append(
      "quick_add_reservation",
      product?.quick_add_reservation ? "1" : "0"
    );

    //

    if (selectedSkuCodes && selectedSkuCodes?.length > 0) {
      // Iterate over the array and append each value
      selectedSkuCodes?.forEach((skuCode, index) => {
        form_data.append(`sku_code[${index}]`, skuCode);
      });
    }
    form_data.append("track_stock_quantity", "1");
    // form_data.append(
    //   'track_stock_quantity',
    //   values?.track_stock_quantity ? '1' : '0'
    // );
    form_data.append("current_stock_quantity", values?.current_stock_quantity);
    form_data.append("supplier_id", values?.supplier_id);
    form_data.append(
      "reorder_stock_quantity",
      values?.reorder_stock_quantity || 0
    );
    if (values?.stock_locations?.some((x: any) => !!x?.stock_quantity)) {
      const locationsStock = values?.stock_locations?.filter(
        (x: any) => !!x?.stock_quantity
      );
      locationsStock?.forEach(
        (location: { location_id: any; stock_quantity: any }, index: any) => {
          if (location?.stock_quantity) {
            form_data.append(
              `stock_locations[${index}][location_id]`,
              location?.location_id
            );
            form_data.append(
              `stock_locations[${index}][stock_quantity]`,
              location?.stock_quantity
            );
          }
        }
      );
    } else {
      form_data.append(`stock_locations[${0}][location_id]`, locationId);
      form_data.append(
        `stock_locations[${0}][stock_quantity]`,
        values?.reorder_stock_quantity
      );
    }
    // values?.stock_locations?.length ?  values?.stock_locations?.forEach((location: { location_id: any; stock_quantity: any; }, index: any) => {
    //   if (location?.location_id && location?.stock_quantity) {
    //     form_data.append(`stock_locations[${index}][location_id]`, location?.location_id);
    //     form_data.append(`stock_locations[${index}][stock_quantity]`, location?.stock_quantity);
    //   }
    // });
    form_data.append(
      "low_stock_level_notification",
      values?.low_stock_level_notification ? "1" : "0"
    );
    form_data.append("low_stock_level", values?.low_stock_level || 0);

    if (update) {
      onEditProduct(form_data);
    } else {
      onAddProduct(form_data);
    }
  };

  const { data: listLocations } = useQuery([queryKeys.listLocations], () =>
    getAllLocations()
  );

  const [initialStockLocations, setInitialStockLocations] = useState([]);
  useEffect(() => {
    // Fetch locations and set initial values for stock_locations
    const fetchLocations = async () => {
      try {
        const response = await getAllLocations();
        const locations = response.data || [];
        const initialStockLocations = locations.map(
          (location: { id: any }, i: number) => ({
            location_id: location?.id,
            stock_quantity: product?.locations?.find(
              (loc) => location?.id === loc?.id
            )?.pivot?.stock_quantity,
          })
        );

        setInitialStockLocations(initialStockLocations);
        form.setFieldsValue({
          stock_locations: initialStockLocations,
        });

        // Set initial values for stock_locations in the form
        // if (product?.sku_code) {
        //   const initialSku = product?.sku_code.map((sku: any) => ({
        //     sku_code: sku,
        //   }));

        //   setselectedSku(initialSku);
        // }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();
  }, [form]);

  const isLowStockLevel = Form.useWatch("low_stock_level_notification", form);
  const intl = useIntl();
  return (
    <>
      <Modal
        // width="50%"
        className="w-full md:w-1/2"
        open={supplierVisible}
        onCancel={() => setSupplierVisible(false)}
        footer={null}
        destroyOnClose
      >
        <ModalForm
          close={() => setSupplierVisible(false)}
          setSupplierId={(id: number) => {
            if (!id) return;
            form.setFieldsValue({ supplier_id: id });
          }}
        />
      </Modal>
      <Form
        onFinish={onFinish}
        onValuesChange={(changedValues: Partial<productType>, values: any) => {
          setProduct({ ...product, ...changedValues });
        }}
        autoComplete="off"
        layout="vertical"
        className="w-full"
        form={form}
        initialValues={{
          enable_online_booking: product?.enable_online_booking || false,
          low_stock_level_notification:
            product?.low_stock_level_notification || false,
          track_stock_quantity: product?.track_stock_quantity || false,
          current_stock_quantity: product?.current_stock_quantity,
          supplier_id: product?.supplier_id,
          low_stock_level: product?.low_stock_level || 1,
          reorder_stock_quantity: product?.reorder_stock_quantity || 1,
          // stock_locations: product?.locations || [],
          stock_locations: listLocations?.data?.map((location: Location) => ({
            location_id: location?.id,
          })),
          sku: [{}],
        }}
      >
        <Form.List name="sku">
          {(fields, { add, remove }) => (
            <>
              <Form.Item className="p-2 rounded bg-color-light-gr w-full text-color-primary">
                {fields.map(({ key, name, ...restField }, index) => (
                  <Row key={key} gutter={{ xs: 8, sm: 16, md: 24, lg: 15 }}>
                    {selectedSku.map((sku, index) => (
                      <Col
                        className="gutter-row"
                        xl={10}
                        lg={12}
                        xs={24}
                        sm={12}
                        span={12}
                      >
                        <Form.Item
                          required={false}
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({
                                id: "sku_required_error_message",
                              }),
                            },
                          ]}
                          label="SKU"
                        >
                          <Input
                            placeholder={intl.formatMessage({
                              id: "sku_placeholder",
                            })}
                            className="w-full my-input cursor-auto"
                            defaultValue={sku.sku_code}
                            disabled
                            suffix={
                              <div
                                onClick={() => handleRemoveSku(index)}
                                className="cursor-pointer"
                              >
                                <DeleteSVG />
                                {/* <Button
                                  className="border-none"
                                  onClick={() => handleRemoveSku(index)}
                                  icon={<DeleteSVG />}
                                  type="text"
                                ></Button> */}
                              </div>
                            }
                          />
                        </Form.Item>
                      </Col>
                    ))}
                    <Col
                      className="gutter-row"
                      xl={10}
                      lg={12}
                      xs={24}
                      sm={12}
                      span={12}
                    >
                      <Form.Item
                        {...restField}
                        required={false}
                        name={[name, "sku_code"]}
                        label=" "
                        // initialValue={generateSku}
                      >
                        <Input
                          placeholder={intl.formatMessage({
                            id: "sku_placeholder",
                          })}
                          className="w-full my-input"
                          //   suffix={
                          //     <button className="text-sm text-[#00086D] border-none bg-transparent" onClick={() => {
                          //       const generatedSKU = generateSKUCode();
                          //       setGenerateSku(generatedSKU);
                          //     }}>انشاء تلقائي</button>
                          // }
                        />
                      </Form.Item>
                    </Col>

                    <Col
                      className="gutter-row"
                      xl={4}
                      lg={12}
                      xs={24}
                      sm={12}
                      span={12}
                    >
                      <Form.Item label=" ">
                        <Button
                          className="bg-transparent"
                          onClick={() => {
                            add();
                            const pricingData = form.getFieldValue([
                              "sku",
                              index,
                            ]);
                            const newSkuCodes = [...selectedSku, pricingData];

                            if (pricingData?.sku_code) {
                              setselectedSku(newSkuCodes);
                            }

                            remove(index);
                          }}
                          block
                          icon={<PlusSquareOutlined />}
                        >
                          SKU
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </Form.Item>
            </>
          )}
        </Form.List>

        {/* <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col className="gutter-row" xl={19} lg={12} xs={24} sm={12} span={12}>
          <label className="text-base font-semibold mt-3 w-full">
            تفعيل خاصية تتبع أعداد المنتج
          </label>
        </Col>
        <Col className="gutter-row" xl={5} lg={12} xs={24} sm={12} span={12}>
          <Form.Item name="track_stock_quantity">
            <Switch
              // className="custom-switch-text"
              // checkedChildren={
              //   <span style={{ color: '#00086D' }}>التفعيل</span>
              // }
              // unCheckedChildren={
              //   <span style={{ color: 'gray' }}>عدم التفعيل</span>
              // }
            />
          </Form.Item>
        </Col>
      </Row> */}

        <div className="p-2 grid grid-cols-2 items-center gap-4 flex-wrap rounded bg-color-light-gr w-full text-[#FBFBF] mb-5">
          <div className="col-span-2">
            <p>{intl.formatMessage({ id: "current_inventory_title" })}</p>
          </div>

          <div>
            <Form.Item
              name="current_stock_quantity"
              label={intl.formatMessage({ id: "current_stock_label" })}
              required={false}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "current_stock_required_message",
                  }),
                },
                {
                  validator(rule, value, callback) {
                    if (value <= 0) {
                      return Promise.reject(
                        intl.formatMessage({
                          id: "current_stock_validation_message",
                        })
                      );
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <Input
                type="number"
                min={0}
                placeholder={intl.formatMessage({
                  id: "current_stock_placeholder",
                })}
              />
            </Form.Item>
          </div>
          <div>
            <Form.Item
              required={false}
              name={"supplier_id"}
              label={intl.formatMessage({ id: "supplier_label" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "supplier_required_message",
                  }),
                },
              ]}
            >
              <Select
                placeholder={intl.formatMessage({ id: "supplier_placeholder" })}
                className="w-full my-input"
                dropdownRender={(menu: any) => (
                  <>
                    {menu}
                    <Button
                      onClick={() => setSupplierVisible(true)}
                      icon={<PlusOutlined />}
                      type="primary"
                      className="w-full btn-primary-light border-0"
                      style={{ justifyContent: "flex-start" }}
                      size="small"
                    >
                      {intl.formatMessage({ id: "add_supplier_button" })}
                    </Button>
                  </>
                )}
                loading={isLoadingSuppliers}
                disabled={!!isLoadingSuppliers}
                options={listSuppliers?.data.map(
                  (item: { id: any; name: string }) => ({
                    value: item.id,
                    label: item.name,
                  })
                )}
              />
            </Form.Item>
          </div>

          <Form.List name="stock_locations">
            {(fields) => (
              <Row>
                <Form.Item>
                  <div className=" flex justify-center gap-4 flex-wrap  items-center  ">
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div className="flex flex-col ">
                        <div className="w-full ">
                          <Form.Item
                            {...restField}
                            required={false}
                            label={intl.formatMessage({ id: "location_label" })}
                            className="px-3"
                            name={[name, "location_id"]}
                          >
                            <Select
                              placeholder={intl.formatMessage({
                                id: "location_placeholder",
                              })}
                              className="bg-[#EEF0FF] rounded-md"
                              disabled
                              // style={{ width: "100%" }}
                              suffixIcon={""}
                              optionLabelProp="label"
                            >
                              {listLocations?.data?.map((location: any) => (
                                <Select.Option
                                  key={location.id}
                                  value={location.id}
                                  label={
                                    <span>
                                      <span className="">
                                        <SalonSVG />
                                      </span>
                                      <span className="text-base">
                                        {" "}
                                        {location.name}
                                      </span>
                                    </span>
                                  }
                                >
                                  {location.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>
                        <div className="w-full">
                          <Form.Item
                            {...restField}
                            className="px-3 "
                            label={intl.formatMessage({
                              id: "stock_quantity_label",
                            })}
                            required={false}
                            name={[name, "stock_quantity"]}
                            dependencies={["current_stock_quantity"]}
                            rules={[
                              {
                                required: key === 0 ? true : false,
                                message: intl.formatMessage({
                                  id: "stock_quantity_required_message",
                                }),
                              },
                              {
                                validator(rule, value, callback) {
                                  if (value && +value < 0) {
                                    return Promise.reject(
                                      intl.formatMessage({
                                        id: "stock_quantity_positive_message",
                                      })
                                    );
                                  } else if (
                                    value &&
                                    +value >
                                      form.getFieldValue(
                                        "current_stock_quantity"
                                      )
                                  ) {
                                    return Promise.reject(
                                      intl.formatMessage({
                                        id: "stock_quantity_max_message",
                                      })
                                    );
                                  } else {
                                    return Promise.resolve();
                                  }
                                },
                              },
                            ]}
                            initialValue={form.getFieldValue(
                              "current_stock_quantity"
                            )}
                          >
                            <Input
                              type="number"
                              placeholder={intl.formatMessage({
                                id: "current_stock_placeholder",
                              })}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    ))}
                  </div>
                </Form.Item>
              </Row>
            )}
          </Form.List>
        </div>
        <Row
          gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}
          className="p-2 rounded bg-color-light-gr w-full text-color-primary mb-5"
        >
          <Col className="gutter-row mb-5" xl={24} lg={8} xs={24} sm={24}>
            {intl.formatMessage({ id: "inventory_settings_title" })}
          </Col>

          <Col xl={24}>
            <Row
              gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
              className="items-baseline"
            >
              <Col
                className="gutter-row"
                xl={19}
                lg={12}
                xs={24}
                sm={12}
                span={12}
              >
                <label className="  mt-3 w-full text-black">
                  {intl.formatMessage({
                    id: "low_stock_notification_feature_label",
                  })}
                </label>
              </Col>
              <Col
                className="gutter-row"
                xl={5}
                lg={12}
                xs={24}
                sm={12}
                span={12}
              >
                <Form.Item
                  name="low_stock_level_notification"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          {isLowStockLevel && (
            <>
              <Col className="gutter-row" xl={12} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="low_stock_level"
                  label={intl.formatMessage({ id: "low_stock_level_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "current_stock_required_message",
                      }),
                    },
                    {
                      validator(rule, value, callback) {
                        if (value && +value < 1) {
                          return Promise.reject(
                            intl.formatMessage({
                              id: "low_stock_level_validation_message",
                            })
                          );
                        } else {
                          return Promise.resolve();
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    min={1}
                    placeholder={intl.formatMessage({
                      id: "low_stock_level_placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={12} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="reorder_stock_quantity"
                  label={intl.formatMessage({
                    id: "reorder_stock_quantity_label",
                  })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "current_stock_required_message",
                      }),
                    },
                    {
                      validator(rule, value, callback) {
                        if (value && +value < 1) {
                          return Promise.reject(
                            intl.formatMessage({
                              id: "reorder_stock_quantity_validation_message",
                            })
                          );
                        } else {
                          return Promise.resolve();
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    min={0}
                    placeholder={intl.formatMessage({
                      id: "reorder_stock_quantity_placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Flex gap="middle" align="center" justify="center">
          <Button type="primary" loading={isLoading} block htmlType="submit">
            {update
              ? intl.formatMessage({ id: "update_product_button_label" })
              : intl.formatMessage({ id: "add_product_button_label" })}
          </Button>
          <Button
            type="primary"
            disabled={isLoading}
            onClick={onClose}
            block
            className="btn-primary-light"
            htmlType="button"
          >
            {intl.formatMessage({ id: "cancel_button_label" })}
          </Button>
        </Flex>
      </Form>
    </>
  );
}

export default PriceOption;
